/**
 * CAP-backed app-owned entities — the `sap` mode reads these from the ppm-cap
 * service (`/ppm`, HANA Cloud) instead of Supabase. Plan §9B / step 2 of the
 * disconnect plan.
 *
 * Each function is a thin call to the generic capClient. CAP serves camelCase
 * matching the app types, so the generic row mapping (ID→id, <assoc>_ID→<assoc>Id)
 * is all that's needed. Adding another entity is one line here + one registry
 * entry in config.ts + one dispatch line in dataService.ts.
 *
 * NOT here (intentionally): projects/WBS/actuals — those resolve via the direct
 * S/4 read services (actuals.ts, wbs.ts, projects.ts), which build the nested
 * budget/schedule shapes and/or read live ACDOCA. In the full CAP deployment
 * CAP federates those server-side; the React seam doesn't care which.
 */

import type { Risk, Milestone, Resource, ResourceCategory, BaselineModel } from '@/types/ppm'
import type { ForecastVersion } from '@/types/forecasting'
import { capList, capListByFk } from '../capClient'

type Row = Record<string, unknown>

// --- project-scoped reads (filter on project_ID) ---
export const getRisks = (projectId?: string) => capListByFk<Risk>('Risks', 'project_ID', projectId)
export const getMilestones = (projectId?: string) => capListByFk<Milestone>('Milestones', 'project_ID', projectId)
export const getIssues = (projectId?: string) => capListByFk<Row>('Issues', 'project_ID', projectId)
export const getResourceAssignments = (projectId?: string) => capListByFk<Row>('ResourceAssignments', 'project_ID', projectId)
export const getBaselineModels = (projectId?: string) => capListByFk<BaselineModel>('BaselineModels', 'project_ID', projectId)
export const getForecastVersions = (projectId?: string) => capListByFk<ForecastVersion>('ForecastVersions', 'project_ID', projectId)
export const getBudgetData = (projectId?: string) => capListByFk<Row>('BudgetData', 'project_ID', projectId)

// --- portfolio-scoped reads (filter on portfolio_ID) ---
export const getResourceDemand = (portfolioId?: string) => capListByFk<Row>('ResourceDemands', 'portfolio_ID', portfolioId)
export const getScenarios = (portfolioId?: string) => capListByFk<Row>('Scenarios', 'portfolio_ID', portfolioId)
export const getStrategicPriorities = (portfolioId?: string) => capListByFk<Row>('StrategicPriorities', 'portfolio_ID', portfolioId)

// --- unscoped list reads ---
export const getResources = () => capList<Resource>('Resources')
export const getResourceCategories = () => capList<ResourceCategory>('ResourceCategories')
export const getMasterContracts = () => capList<Row>('MasterContracts')
export const getOrganizations = () => capList<Row>('Organizations')
export const getNotifications = () => capList<Row>('Notifications')
